import styled from '@emotion/styled'
import { NotificationPresenter } from 'shared/presenters/NotificationPresenter'
import Icon from 'shared/library/atoms/Icon'
import { Text } from 'library/atoms/Typography'
import StyledReadDot from 'library/atoms/UnreadNotificationDot'
import { AnimatePresence } from 'framer-motion'
import Link from 'next/link'

const NotificationWrapper = ({
  ctaLink,
  children
}: {
  ctaLink?: NotificationPresenter['cta_link']
  children: React.ReactNode
}) => {
  return ctaLink ? (
    <Link href={ctaLink} passHref>
      {children}
    </Link>
  ) : (
    <>{children}</>
  )
}

const Notification = ({
  notification,
  ...other
}: {
  notification: NotificationPresenter
}) => {
  return (
    <NotificationWrapper ctaLink={notification?.cta_link}>
      <StyledNotification {...other} ctaLink={notification?.cta_link}>
        <StyledIconContainer>
          <StyledIcon size={24} name={notification.iconName()} />
          <AnimatePresence>
            {!notification.read && <StyledReadDot />}
          </AnimatePresence>
        </StyledIconContainer>
        <StyledMessage
          fontStyle="p4"
          dangerouslySetInnerHTML={{ __html: notification.message }}
        />
        <StyledTime fontStyle="h5">
          {notification.createdAtTimeFromNow()}
        </StyledTime>
      </StyledNotification>
    </NotificationWrapper>
  )
}

export default Notification

const StyledNotification = styled.span<{
  ctaLink?: NotificationPresenter['cta_link']
}>(({ theme, ctaLink }) => ({
  padding: theme.mixins.pxSpread([theme.space[3], theme.space[5]]),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.space[4],
  marginBottom: theme.space[5],
  marginTop: theme.space[3],
  cursor: ctaLink ? 'pointer' : 'inherit',
  textDecoration: 'none',
  ...theme.mixins.fadeIn
}))

const StyledIconContainer = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 40,
  height: 40,
  boxShadow: theme.shadows[0],
  borderRadius: theme.radii[2],
  position: 'relative'
}))

const StyledIcon = styled(Icon)(() => ({
  flex: 1
}))

const StyledMessage = styled(Text)(() => ({
  flex: 8
}))

const StyledTime = styled(Text)(() => ({
  flex: 1
}))
